import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Settings, PenTool, Loader2 } from 'lucide-react';
import api from '../services/api';

const TOOL_CONFIG = {
  cursive: {
    name: 'Cursive Handwriting',
    description: 'Create beautiful cursive practice pages',
    hasText: true,
    defaultText: 'The quick brown fox jumps over the lazy dog.'
  },
  print: {
    name: 'Print Handwriting',
    description: 'Generate professional print practice sheets',
    hasText: true,
    defaultText: 'Pack my box with five dozen liquor jugs.'
  },
  'name-tracing': {
    name: 'Name Tracing',
    description: 'Create personalized name tracing pages',
    hasText: true,
    defaultText: 'Your Name'
  },
  'tracing-lines': {
    name: 'Tracing Lines',
    description: 'Pre-writing skills practice',
    hasText: false
  },
  alphabet: {
    name: 'Alphabet Tracing',
    description: 'A-Z letter practice worksheets',
    hasText: false,
    hasCase: true
  },
  numbers: {
    name: 'Number Tracing',
    description: 'Numbers 0-9 practice worksheets',
    hasText: false
  }
};

export default function WorksheetGenerator() {
  const { toolType } = useParams();
  const config = TOOL_CONFIG[toolType] || TOOL_CONFIG.print;

  const [settings, setSettings] = useState({
    text: config.defaultText || '',
    fontSize: 24,
    lineSpacing: 1.5,
    penType: 'standard',
    showGuides: true,
    uppercase: true,
    lowercase: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/worksheets/generate', {
        toolType,
        ...settings
      }, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bastion-${toolType}-worksheet.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      setError('Failed to generate worksheet. Please try again.');
      console.error('Generate error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="section-title">{config.name}</h1>
          <p className="section-subtitle">{config.description}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Settings Panel */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="flex items-center gap-2 mb-6">
                <Settings className="w-5 h-5 text-bastion-black" />
                <h2 className="text-lg font-semibold">Worksheet Settings</h2>
              </div>

              <div className="space-y-6">
                {/* Text Input */}
                {config.hasText && (
                  <div>
                    <label className="label">Practice Text</label>
                    <textarea
                      value={settings.text}
                      onChange={(e) => setSettings({ ...settings, text: e.target.value })}
                      className="input min-h-[100px]"
                      placeholder="Enter text to practice..."
                      maxLength={500}
                    />
                    <p className="text-sm text-bastion-gray-500 mt-1">
                      {settings.text.length}/500 characters
                    </p>
                  </div>
                )}

                {/* Case Options for Alphabet */}
                {config.hasCase && (
                  <div>
                    <label className="label">Letter Case</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={settings.uppercase}
                          onChange={(e) => setSettings({ ...settings, uppercase: e.target.checked })}
                          className="w-4 h-4 text-bastion-black rounded"
                        />
                        <span>Uppercase (A-Z)</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={settings.lowercase}
                          onChange={(e) => setSettings({ ...settings, lowercase: e.target.checked })}
                          className="w-4 h-4 text-bastion-black rounded"
                        />
                        <span>Lowercase (a-z)</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Font Size */}
                <div>
                  <label className="label">Font Size: {settings.fontSize}pt</label>
                  <input
                    type="range"
                    min="16"
                    max="48"
                    value={settings.fontSize}
                    onChange={(e) => setSettings({ ...settings, fontSize: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-bastion-gray-500">
                    <span>Small</span>
                    <span>Large</span>
                  </div>
                </div>

                {/* Line Spacing */}
                <div>
                  <label className="label">Line Spacing: {settings.lineSpacing}x</label>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.25"
                    value={settings.lineSpacing}
                    onChange={(e) => setSettings({ ...settings, lineSpacing: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-bastion-gray-500">
                    <span>Compact</span>
                    <span>Spacious</span>
                  </div>
                </div>

                {/* Show Guidelines */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.showGuides}
                      onChange={(e) => setSettings({ ...settings, showGuides: e.target.checked })}
                      className="w-4 h-4 text-bastion-black rounded"
                    />
                    <span className="font-medium">Show guide lines</span>
                  </label>
                  <p className="text-sm text-bastion-gray-500 mt-1">
                    Displays cap height, x-height, and descender lines
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Pen Type & Generate */}
          <div className="space-y-6">
            {/* Pen Type Selection */}
            <div className="card">
              <div className="flex items-center gap-2 mb-4">
                <PenTool className="w-5 h-5 text-bastion-black" />
                <h2 className="text-lg font-semibold">Pen Type</h2>
              </div>

              <div className="space-y-3">
                {[
                  { id: 'standard', name: 'Standard', desc: 'Works with any pen' },
                  { id: 'fountain', name: 'Fountain Pen', desc: 'Optimized line weights' },
                  { id: 'ballpoint', name: 'Ballpoint', desc: 'Steady pressure practice' }
                ].map((pen) => (
                  <label
                    key={pen.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      settings.penType === pen.id
                        ? 'border-bastion-black bg-bastion-black/5'
                        : 'border-bastion-gray-200 hover:border-bastion-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="penType"
                      value={pen.id}
                      checked={settings.penType === pen.id}
                      onChange={(e) => setSettings({ ...settings, penType: e.target.value })}
                      className="mt-1"
                    />
                    <div>
                      <span className="font-medium block">{pen.name}</span>
                      <span className="text-sm text-bastion-gray-500">{pen.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="btn-primary w-full py-4 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Download PDF
                </>
              )}
            </button>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            <p className="text-sm text-bastion-gray-500 text-center">
              Free, unlimited downloads. No signup required.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
